import React, { useState, useRef, useEffect } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Settings,
  CheckCircle2,
  Clock,
  AlertCircle,
  Circle,
  List,
  LayoutGrid,
  ChevronRight,
  ChevronLeft,
  Target,
  Megaphone,
  Globe,
  Share2,
  PenTool,
  Zap,
  Rocket,
  FileText,
  Image,
  Monitor,
  Users,
  TrendingUp,
  Calendar,
  MapPin,
  ArrowRight,
  RefreshCw,
  Kanban,
  Plus,
  Edit,
  Trash2,
  XCircle,
  PlayCircle,
  ArrowLeft,
  Building2,
} from "lucide-react";
import { SetupIcon } from "../../components/icons/SetupIcon.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/Card";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { Progress } from "../../components/Progress";
import { CustomSelector } from "../../components/CustomSelector";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { KanbanView } from "../Projects/components/KanbanView";
import { patchWithRefresh } from "../../api-client";
import { EditTaskModal } from "./EditTaskModal";
import { isClientReadOnly } from "@/utils/clientAccess";
import { SetupDetailView } from "./SetupDetailView";
import SuccessPopup from "../../components/SuccessPopup";

import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { ProjectCard } from "../../components/ProjectCard";
import { TechnicalSetupTableView } from "./TechnicalSetupTableView";
import { DataTableToolbar, toolbarViewModeButtonSx } from "../../components/DataTable";
import { ViewModeSelector } from "../../components/ViewModeSelector";
import apiClient from "../../api-client";
import { ConfirmModal } from "../../components/ConfirmModal";
import { FEATURE_RECURRING_PROJECTS, canSeeContractedPack, getSetupUnavailableMessage, hasAgencyFeature } from "@/utils/agencyFeatures";

const FilterIndicatorDot = ({ theme }) => (
  <Box
    sx={{
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      flexShrink: 0,
    }}
  />
);

const getFilterButtonStyles = (isSelected, theme, neutrals) => ({
  borderRadius: "999px",
  px: 1.5,
  py: 0.5,
  fontSize: "0.8125rem",
  minHeight: "32px",
  backgroundColor: isSelected ? neutrals.white : "transparent",
  color: isSelected ? neutrals.gray[700] : neutrals.gray[600],
  border: `1px solid ${isSelected ? neutrals.gray[200] : "transparent"}`,
  "&:hover": {
    backgroundColor: isSelected ? neutrals.gray[50] : neutrals.gray[100],
  },
});

const COMPLETED_TASK_STATUSES = new Set(["finalizado", "completed"]);
const EXCLUDED_PROGRESS_STATUSES = new Set(["no_necesario"]);

const setupStateColorMap = {
  completed: "green",
  in_progress: "yellow",
  not_started: "blue",
  cancelled: "red",
  no_necesario: "gray",
};

const setupStateLabels = {
  completed: "Completado",
  in_progress: "En progreso",
  not_started: "No iniciado",
  cancelled: "Cancelado",
  no_necesario: "No necesario",
};

const projectStatusFilterOptions = [
  { value: "in_progress", label: "En progreso" },
  { value: "not_started", label: "No iniciados" },
  { value: "completed", label: "Completados" },
  { value: "__all__", label: "Todos" },
];

const SETUP_CARDS_PAGE_SIZE = 40;
const SETUP_TABLE_DEFAULT_PAGE_SIZE = 25;
const TECHNICAL_SETUP_DEFAULT_VIEW = "table";

const SETUP_VIEW_MODE_OPTIONS = [
  { value: "cards", label: "Tarjetas", icon: LayoutGrid },
  { value: "table", label: "Tabla", icon: List },
];

const createEmptyAdvancedFilters = () => ({
  agency: null,
  client: null,
});

const cloneAdvancedFilters = (filters) => ({
  agency: filters.agency ? { ...filters.agency } : null,
  client: filters.client ? { ...filters.client } : null,
});

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const CLIENT_NO_SETUP_PROJECT_MESSAGE = "client does not have a setup project";

const getApiErrorMessage = (error) => {
  if (!error) return "";
  const responseData = error?.response?.data;
  const extractString = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return "";
    }
  };

  if (responseData) {
    const detail = responseData?.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      const detailString = extractString(detail);
      if (detailString) return detailString;
    }
    const message = responseData?.message;
    if (typeof message === "string") return message;
    const messageString = extractString(message);
    if (messageString) return messageString;
    const responseString = extractString(responseData);
    if (responseString) return responseString;
  }

  if (typeof error?.message === "string") return error.message;
  return "";
};

// Vista global de proyectos (cuando no hay cliente seleccionado)
function GlobalProjectsView({
  projects,
  onProjectClick,
  statusStats,
  theme,
  isLoading,
  isLoadingMore,
  isFetching,
  hasMore,
  loadMoreRef,
  selectedStatus,
  onStatusChange,
  searchInput,
  onSearchInputChange,
  filterChips = [],
  onOpenFilterModal,
  filterModalProps,
  onStateChange,
  viewMode,
  onViewModeChange,
  showViewModeSelector = true,
  totalProjects,
  currentPage,
  rowsPerPage,
  onTablePageChange,
  onTableRowsPerPageChange,
}) {
  const progressActionIcon = React.useMemo(() => <ArrowRight size={14} />, []);
  const { user: currentUser } = useAuth();
  const showPack = canSeeContractedPack(currentUser);

  const toolbarProps = {
    searchValue: searchInput,
    onSearchChange: onSearchInputChange,
    searchPlaceholder: "Buscar setup...",
    tabs: [
      { value: "__all__", label: "Todos", count: statusStats.all },
      { value: "in_progress", label: "En progreso", count: statusStats.in_progress },
      { value: "not_started", label: "No iniciados", count: statusStats.not_started },
      { value: "completed", label: "Completados", count: statusStats.completed },
      { value: "no_necesario", label: "No necesario", count: statusStats.no_necesario },
    ],
    activeTab: selectedStatus,
    onTabChange: onStatusChange,
    filterChips,
    onFilterClick: onOpenFilterModal,
    filterTooltip: "Editar filtros",
    toolbarEnd: showViewModeSelector ? (
      <ViewModeSelector
        options={SETUP_VIEW_MODE_OPTIONS}
        value={viewMode}
        onChange={onViewModeChange}
        tooltipTitle="Escoger vista"
        buttonSx={toolbarViewModeButtonSx}
      />
    ) : null,
  };

  return (
    <Box>
      {viewMode !== "table" ? (
        <DataTableToolbar variant="standalone" sx={{ mb: 3 }} {...toolbarProps} />
      ) : null}

      {viewMode === "table" ? (
        <TechnicalSetupTableView
          rows={projects}
          totalCount={typeof totalProjects === "number" ? totalProjects : projects.length}
          isFetching={isFetching}
          currentPage={Math.max(currentPage - 1, 0)}
          rowsPerPage={rowsPerPage}
          onPageChange={onTablePageChange}
          onRowsPerPageChange={onTableRowsPerPageChange}
          onViewProgress={onProjectClick}
          onStateChange={onStateChange}
          toolbarProps={toolbarProps}
        />
      ) : isLoading ? (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={`project-skeleton-${index}`}>
              <CardContent sx={{ padding: "10px", width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="rounded" width={80} height={24} />
                </Box>
                <Skeleton variant="text" width="40%" height={18} />
                <Skeleton
                  variant="text"
                  width="55%"
                  height={18}
                  sx={{ mt: 1 }}
                />
                <Skeleton variant="rounded" height={8} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : !projects || projects.length === 0 && !isFetching ? (
        <Card>
          <Box sx={{ p: 6 }}>
            <EmptyStateMessage
              icon={Settings}
              message="No hay proyectos de setup"
              iconSize={48}
            />
          </Box>
        </Card>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
          }}
        >
          {projects.map((project) => (
            <Box key={project.id} sx={{ width: "100%" }}>
              <ProjectCard
                project={project}
                onClick={onProjectClick}
                onStateChange={
                  isClientReadOnly(currentUser, project.client) ? undefined : onStateChange
                }
                readOnly={isClientReadOnly(currentUser, project.client)}
                allowNotNeeded
                title={project.client_name}
                infoItems={[
                  ...(!showPack
                    ? []
                    : project.setup_variant_display
                      ? [{ icon: SetupIcon, text: project.setup_variant_display }]
                      : [{
                        icon: SetupIcon,
                        text: "Ningún pack contratado",
                        textColor: theme.palette.error.main,
                        fontWeight: 500,
                      }]),
                  ...(project.start_date
                    ? [
                      {
                        icon: Calendar,
                        text: new Date(project.start_date).toLocaleDateString(
                          "es-ES"
                        ),
                      },
                    ]
                    : []),
                ]}
                showActionButton={true}
                actionButtonLabel="Progreso"
                actionButtonIcon={progressActionIcon}
                onActionClick={onProjectClick}
              />
            </Box>
          ))}
          {isLoadingMore &&
            hasMore &&
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={`setup-skeleton-more-${index}`}>
                <CardContent sx={{ padding: "10px", width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                  </Box>
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton
                    variant="text"
                    width="55%"
                    height={18}
                    sx={{ mt: 1 }}
                  />
                  <Skeleton variant="rounded" height={8} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            ))}
        </Box>
      )}
      {hasMore && viewMode === "cards" && <Box ref={loadMoreRef} sx={{ height: 1 }} />}

      {filterModalProps && <IncidentFiltersModal {...filterModalProps} />}
    </Box>
  );
}

export function TechnicalSetup() {
  const { user: currentUser, interceptorReady } = useAuth();
  const completeSetupConfirmText = hasAgencyFeature(currentUser, FEATURE_RECURRING_PROJECTS)
    ? "¿Seguro que quieres marcar este setup como completado? Esto activará el plan de recurrencia, que consiste en crear proyectos mensuales para el cliente."
    : "¿Seguro que quieres marcar este setup como completado?";
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    technicalSetupRefreshKey,
    filterRefresh,
    Clientes_prop,
    isMultiClientUser,
  } = useOutletContext();
  const { projectId: urlProjectId } = useParams(); // Obtener projectId de la URL
  const navigate = useNavigate(); // Para navegar a la nueva ruta
  const [phases, setPhases] = useState([]);
  const [projectProgress, setProjectProgress] = useState(0);
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const isClientUser = currentUser?.role === "client";

  // Estados para el selector de cliente (superadmin)
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("__all__");
  const [statusStats, setStatusStats] = useState({
    in_progress: 0,
    not_started: 0,
    completed: 0,
    no_necesario: 0,
    all: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const searchDebounceTimerRef = useRef(null); // Ref para el timer del debounce
  const lastSearchRef = useRef(""); // Ref para trackear la última búsqueda ejecutada
  const [searchRefreshKey, setSearchRefreshKey] = useState(0); // Key para forzar refetch en búsqueda
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [filterModalRefreshKey, setFilterModalRefreshKey] = useState(0);
  const [advancedFiltersRefreshKey, setAdvancedFiltersRefreshKey] = useState(0);
  const [globalProjectsPage, setGlobalProjectsPage] = useState(1);
  const [globalProjectsList, setGlobalProjectsList] = useState([]);
  const [hasMoreGlobalProjects, setHasMoreGlobalProjects] = useState(true);
  const [totalGlobalProjects, setTotalGlobalProjects] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(
    SETUP_TABLE_DEFAULT_PAGE_SIZE
  );
  const resolvedViewMode = isClientUser
    ? "cards"
    : viewMode || TECHNICAL_SETUP_DEFAULT_VIEW;
  const isTableView = resolvedViewMode === "table";
  const currentSetupPageSize = isTableView
    ? tableRowsPerPage
    : SETUP_CARDS_PAGE_SIZE;

  // Ref para trackear el filtro anterior
  const previousFilterRef = useRef(selectedFilter);
  const loadMoreRef = useRef(null);

  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // � CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === '' && lastSearchRef.current !== '') {
        lastSearchRef.current = '';
        setSearchTerm('');
        setGlobalProjectsPage(1);
        setGlobalProjectsList([]);
        setHasMoreGlobalProjects(true);
        setTotalGlobalProjects(null);
        setSearchRefreshKey(prev => prev + 1); // Forzar refetch
        return;
      }

      // 🔹 CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const isAgencyScopedUser = ["agency_admin", "consultant"].includes(
    currentUser?.role
  );
  const shouldFetchModalAgencies =
    isFilterModalOpen && currentUser?.role === "superadmin";

  const derivedAgencyForClients = React.useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === "superadmin") {
      const fallbackAgency =
        appliedAdvancedFilters.agency?.value || selectedFilter || null;
      if (isFilterModalOpen) {
        return draftAdvancedFilters.agency?.value || fallbackAgency;
      }
      return fallbackAgency;
    }
    return currentUser.agencyId || null;
  }, [
    currentUser,
    draftAdvancedFilters.agency,
    appliedAdvancedFilters.agency,
    selectedFilter,
    isFilterModalOpen,
  ]);

  const shouldFetchModalClients =
    isFilterModalOpen &&
    (currentUser?.role === "superadmin" || isAgencyScopedUser);

  const filterClientsEndpoint = React.useMemo(() => {
    const params = new URLSearchParams();
    params.append("ordering", "name");
    if (currentUser?.role === "superadmin") {
      if (derivedAgencyForClients) {
        params.append("agency", derivedAgencyForClients);
      }
    }
    // Note: For non-superadmin, backend already filters by user's agency/access
    const queryString = params.toString();
    return `/clients/options/${queryString ? `?${queryString}` : ""}`;
  }, [currentUser?.role, derivedAgencyForClients]);

  const { data: filterAgenciesData, isLoading: isLoadingFilterAgencies } =
    getData("/agencies/", interceptorReady && shouldFetchModalAgencies, [
      technicalSetupRefreshKey,
      filterRefresh,
      filterModalRefreshKey,
    ]);

  const { data: filterClientsData, isLoading: isLoadingFilterClients } =
    getData(
      filterClientsEndpoint,
      interceptorReady && shouldFetchModalClients,
      [
        filterClientsEndpoint,
        draftAdvancedFilters.agency?.value,
        currentUser?.agencyId,
        filterModalRefreshKey,
      ]
    );

  const agencyOptions = React.useMemo(() => {
    if (!filterAgenciesData) return [];
    return normalizeApiList(filterAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }, [filterAgenciesData]);

  const filterModalClientOptions = React.useMemo(() => {
    if (!filterClientsData) return [];
    const list = normalizeApiList(filterClientsData);

    const hasAgencySelected = isFilterModalOpen
      ? draftAdvancedFilters.agency?.value
      : appliedAdvancedFilters.agency?.value;

    return list.map((client) => {
      const agencyName =
        client.agency?.name ||
        client.agency_info?.name ||
        client.agency_name ||
        null;

      if (currentUser?.role === "superadmin") {
        const showAgencyInLabel = !hasAgencySelected;
        return {
          value: client.id,
          label:
            showAgencyInLabel && agencyName
              ? `${client.name} - ${agencyName}`
              : client.name,
          agency:
            client.agency ||
            client.agency_id ||
            client.agency?.id ||
            client.agency_info?.id ||
            null,
        };
      }
      return {
        value: client.id,
        label: client.name,
        agency:
          client.agency ||
          client.agency_id ||
          client.agency?.id ||
          client.agency_info?.id ||
          null,
      };
    });
  }, [
    filterClientsData,
    appliedAdvancedFilters.agency,
    draftAdvancedFilters.agency,
    isFilterModalOpen,
    currentUser?.role,
  ]);

  const globalAgencyOption = React.useMemo(() => {
    if (
      currentUser?.role !== "superadmin" ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [currentUser?.role, selectedFilter, selectedFilterData]);

  const globalClientOption = React.useMemo(() => {
    if (!isAgencyScopedUser || !selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [isAgencyScopedUser, selectedFilter, selectedFilterData]);

  const isGlobalAgencyLocked =
    currentUser?.role === "superadmin" && Boolean(globalAgencyOption);
  const isGlobalClientLocked =
    isAgencyScopedUser && Boolean(globalClientOption);

  const advancedAgencyId = appliedAdvancedFilters.agency?.value || null;
  const advancedClientId = appliedAdvancedFilters.client?.value || null;

  const handleOpenFilterModal = () => {
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
    setFilterModalRefreshKey((prev) => prev + 1);
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
  };

  const handleDraftFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => {
      const nextValue = value ? { ...value } : null;
      const updated = { ...prev, [field]: nextValue };
      if (field === "agency" && nextValue) {
        updated.client = null;
      }
      return updated;
    });
  };

  const handleClearDraftFilters = () => {
    setDraftAdvancedFilters(createEmptyAdvancedFilters());
  };

  const handleApplyAdvancedFilters = () => {
    setAppliedAdvancedFilters(cloneAdvancedFilters(draftAdvancedFilters));
    setAdvancedFiltersRefreshKey((prev) => prev + 1);
    setIsFilterModalOpen(false);
  };

  const handleRemoveAdvancedFilter = React.useCallback((key) => {
    setAppliedAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      next[key] = null;
      return next;
    });
    setDraftAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      next[key] = null;
      return next;
    });
    setAdvancedFiltersRefreshKey((prev) => prev + 1);
  }, []);

  const advancedFilterChips = React.useMemo(() => {
    const chips = [];

    if (appliedAdvancedFilters.agency) {
      chips.push({
        key: "agency",
        label: appliedAdvancedFilters.agency.label,
      });
    }

    if (appliedAdvancedFilters.client) {
      chips.push({
        key: "client",
        label: appliedAdvancedFilters.client.label,
      });
    }

    return chips;
  }, [appliedAdvancedFilters]);

  const handleClearGlobalFilter = React.useCallback(() => {
    changeSelectedFilter("global");
  }, [changeSelectedFilter]);

  const globalFilterChip = React.useMemo(() => {
    if (!selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      key: "global-filter",
      label: selectedFilterData.name,
      onRemove: handleClearGlobalFilter,
    };
  }, [selectedFilter, selectedFilterData, handleClearGlobalFilter]);

  const displayedFilterChips = React.useMemo(() => {
    const chips = [];
    if (globalFilterChip) {
      chips.push(globalFilterChip);
    }
    advancedFilterChips.forEach((chip) => {
      chips.push({
        ...chip,
        onRemove: () => handleRemoveAdvancedFilter(chip.key),
      });
    });
    return chips;
  }, [globalFilterChip, advancedFilterChips, handleRemoveAdvancedFilter]);

  const filterConfigs = React.useMemo(
    () => [
      {
        key: "agency",
        title: "Agencia",
        description: "Escoge la agencia sobre la que quieres filtrar.",
        icon: Building2,
        options: agencyOptions,
        loading: isLoadingFilterAgencies,
        disabled: isGlobalAgencyLocked,
        placeholder: "Selecciona una agencia",
        loadingPlaceholder: "Cargando agencias...",
        fallbackOption: globalAgencyOption,
        showForRoles: ["superadmin"],
        role: currentUser?.role,
      },
      {
        key: "client",
        title: "Cliente",
        description: "Filtra los proyectos asociados a un cliente concreto.",
        icon: Users,
        options: filterModalClientOptions,
        loading: isLoadingFilterClients,
        disabled: isGlobalClientLocked,
        placeholder: "Selecciona un cliente",
        loadingPlaceholder: "Cargando clientes...",
        fallbackOption: globalClientOption,
        role: currentUser?.role,
      },
    ],
    [
      agencyOptions,
      filterModalClientOptions,
      isLoadingFilterAgencies,
      isLoadingFilterClients,
      isGlobalAgencyLocked,
      isGlobalClientLocked,
      globalAgencyOption,
      globalClientOption,
      currentUser?.role,
    ]
  );

  // Manejar cambios de filtro para todos los roles
  React.useEffect(() => {
    const filterChanged = previousFilterRef.current !== selectedFilter;

    if (filterChanged) {
      previousFilterRef.current = selectedFilter;

      if (currentUser?.role === "superadmin") {
        setSelectedClient(null);
        // Si estamos en una ruta con projectId, volver a la vista global
        if (urlProjectId) {
          navigate("/setup");
        }
      } else if (
        (currentUser?.role === "agency_admin" ||
          currentUser?.role === "consultant" ||
          (currentUser?.role === "client" && isMultiClientUser)) &&
        urlProjectId
      ) {
        // Para agency_admin y consultant: volver a vista global cuando cambia el filtro si están en un proyecto
        navigate("/setup");
      }

      const hasAdvancedFilters =
        appliedAdvancedFilters.agency?.value ||
        appliedAdvancedFilters.client?.value;

      if (hasAdvancedFilters) {
        setAppliedAdvancedFilters(createEmptyAdvancedFilters());
        setDraftAdvancedFilters(createEmptyAdvancedFilters());
        setAdvancedFiltersRefreshKey((prev) => prev + 1);
      }
    }
  }, [
    selectedFilter,
    currentUser?.role,
    urlProjectId,
    navigate,
    appliedAdvancedFilters,
  ]);

  // Estados para el Kanban
  const [showKanban, setShowKanban] = useState(false);
  const [showPhaseKanban, setShowPhaseKanban] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedPhaseTask, setDraggedPhaseTask] = useState(null);

  // refreshes
  const [phaseTasksRefreshTrigger, setPhaseTasksRefreshTrigger] = useState(0); // Para forzar refetch de tareas de fase EN MODO NORMAL (no kanban)
  const kanbanTaskUpdateRef = useRef(null); // Referencia para actualizar tareas en el kanban
  const [manualProjectRefresh, setManualProjectRefresh] = useState(0); // Solo para refreshes manuales (kanban, selector de clientes en superadmin, etc)
  const [clientRefresh, setClientRefresh] = useState(0); // Para forzar refetch del cliente cuando se cambia de cliente en el selector.

  // Estado para tareas de fase con actualizaciones locales
  const [localPhaseTasks, setLocalPhaseTasks] = useState([]);

  // Estados para el modal de edición de tareas
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [isModalFromKanban, setIsModalFromKanban] = useState(false);

  // Estados para el popup de notificación
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [stateConfirmOpen, setStateConfirmOpen] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);
  const [detailSetupState, setDetailSetupState] = useState(null);
  const [localSetupRefreshKey, setLocalSetupRefreshKey] = useState(0);
  const [localSetupsOverrides, setLocalSetupsOverrides] = useState({});

  // Función para cambiar el estado de un setup
  const handleSetupStateChange = React.useCallback(
    async (projectId, newState, previousState) => {
      // previousState se pasa desde el componente que llama (ProjectCard)
      try {
        await apiClient.patch(`projects/projects/${projectId}/`, {
          state: newState,
        });

        // Actualizar localmente en vez de refrescar
        setLocalSetupsOverrides((prev) => ({
          ...prev,
          [projectId]: { state: newState },
        }));

        // Actualizar statusStats localmente
        if (previousState && previousState !== newState) {
          setStatusStats((prev) => ({
            ...prev,
            [previousState]: Math.max(0, (prev[previousState] || 0) - 1),
            [newState]: (prev[newState] || 0) + 1,
          }));
        }

        const stateLabels = {
          in_progress: "En progreso",
          completed: "Completado",
          not_started: "No iniciado",
          no_necesario: "No necesario",
        };
        setPopupMessage(
          `Setup marcado como ${stateLabels[newState] || newState}`
        );
        setIsError(false);
        setPopupOpen(true);
        return true;
      } catch (error) {
        console.error("Error al cambiar estado del setup:", error);
        setPopupMessage(
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Error al cambiar estado"
        );
        setIsError(true);
        setPopupOpen(true);
        return false;
      }
    },
    []
  );

  const calculateProjectProgress = React.useCallback((phasesToEvaluate) => {
    if (!Array.isArray(phasesToEvaluate) || phasesToEvaluate.length === 0) {
      return null;
    }

    const validTasks = phasesToEvaluate.flatMap((phase) =>
      (phase.tasks || []).filter(
        (task) => task && !EXCLUDED_PROGRESS_STATUSES.has(task.status)
      )
    );

    if (!validTasks.length) {
      return null;
    }

    const completedTasks = validTasks.filter((task) =>
      COMPLETED_TASK_STATUSES.has(task.status)
    ).length;

    return Math.round((completedTasks / validTasks.length) * 100);
  }, []);

  // Funciones para actualizar estados (movidas aquí para que estén disponibles en todo el componente)
  const updatePhaseStatus = async (phaseId, newStatus) => {
    // Encontrar la fase actual para verificar si el estado cambió
    const currentPhase = phases.find((phase) => phase.id === phaseId);

    // Si el estado no cambió, no hacer nada
    if (currentPhase && currentPhase.status === newStatus) {
      return;
    }

    // Actualizar estado local inmediatamente para mejor UX
    setPhases((prevPhases) =>
      prevPhases.map((phase) =>
        phase.id === phaseId ? { ...phase, status: newStatus } : phase
      )
    );

    try {
      // Hacer la llamada a la API
      await patchWithRefresh(`projects/phases/${phaseId}/`, {
        state: newStatus,
      });

      // Si la fase se marcó como completada, actualizar todas las tareas localmente
      // (excepto las que ya están 'finalizado')
      // También actualizar el progreso de la fase al 100%
      if (newStatus === "completed") {
        setPhases((prevPhases) =>
          prevPhases.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 100, // Actualizar progreso al 100%
              tasks: phase.tasks.map((task) => {
                // No modificar tareas que ya están finalizadas
                if (task.status === "finalizado") {
                  return task;
                }
                // Marcar como finalizado
                return { ...task, status: "finalizado" };
              }),
            };
          })
        );
      }

      // Si la fase se marcó como no necesaria, marcar TODAS las tareas localmente
      if (newStatus === "no_necesario") {
        setPhases((prevPhases) =>
          prevPhases.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              tasks: phase.tasks.map((task) => ({ ...task, status: "no_necesario" })),
            };
          })
        );
      }

      // Si la fase se marcó como en progreso, marcar TODAS las tareas como en desarrollo
      if (newStatus === "in_progress") {
        setPhases((prevPhases) =>
          prevPhases.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 0,
              tasks: phase.tasks.map((task) => ({
                ...task,
                status: "en_desarrollo",
              })),
            };
          })
        );
      }

      // Si la fase se marcó como pendiente, marcar TODAS las tareas como planeado
      if (newStatus === "not_started") {
        setPhases((prevPhases) =>
          prevPhases.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 0,
              tasks: phase.tasks.map((task) => ({ ...task, status: "planeado" })),
            };
          })
        );
      }

      // Mostrar popup de éxito
      setPopupMessage("Estado de la fase actualizado correctamente");
      setIsError(false);
      setPopupOpen(true);
    } catch (error) {
      console.error("Error al actualizar el estado de la fase:", error);

      // Revertir el cambio local en caso de error
      setPhases((prevPhases) =>
        prevPhases.map((phase) =>
          phase.id === phaseId
            ? { ...phase, status: currentPhase?.status || phase.status }
            : phase
        )
      );

      // Mostrar popup de error
      setPopupMessage(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Error al actualizar el estado de la fase. Por favor, intenta de nuevo."
      );
      setIsError(true);
      setPopupOpen(true);
    }
  };

  const updateTaskStatus = async (
    phaseId,
    taskId,
    newStatus,
    showPopup = false
  ) => {
    // Encontrar la tarea actual para verificar si el estado cambió
    const currentPhase = phases.find((phase) => phase.id === phaseId);
    const currentTask = currentPhase?.tasks?.find((task) => task.id === taskId);

    // Si el estado no cambió, no hacer nada
    if (currentTask && currentTask.status === newStatus) {
      return true;
    }

    try {
      // Hacer la llamada a la API
      await patchWithRefresh(`projects/tasks/${taskId}/update-state/`, {
        state: newStatus,
      });

      // Actualizar estado local inmediatamente tras confirmación del backend
      setPhases((prevPhases) =>
        prevPhases.map((phase) =>
          phase.id === phaseId
            ? {
              ...phase,
              tasks: phase.tasks?.map((task) =>
                task.id === taskId ? { ...task, status: newStatus } : task
              ),
            }
            : phase
        )
      );

      // Mostrar popup de éxito solo si se solicita (vista de gestión)
      if (showPopup) {
        setPopupMessage("Estado de la tarea actualizado correctamente");
        setIsError(false);
        setPopupOpen(true);
      }

      return true;
    } catch (error) {
      console.error("Error al actualizar el estado de la tarea:", error);

      // Mostrar popup de error solo si se solicita (vista de gestión)
      if (showPopup) {
        setPopupMessage(
          error.response?.data?.detail ||
            error.response?.data?.message ||
            "Error al actualizar el estado de la tarea. Por favor, intenta de nuevo."
        );
        setIsError(true);
        setPopupOpen(true);
      }

      return false;
    }
  };

  // Funciones para manejar la edición de tareas
  const handleEditTask = (task, isFromKanban = false) => {
    setTaskToEdit(task);
    setIsModalFromKanban(isFromKanban);
    setEditTaskModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditTaskModalOpen(false);
    setTaskToEdit(null);
  };

  // lo usamos para actualizar los datos de la tarea en las fases y en el kanban si está abierto
  const handleTaskUpdated = (updatedTaskOrTasks) => {
    const updatesArray = Array.isArray(updatedTaskOrTasks)
      ? updatedTaskOrTasks
      : [updatedTaskOrTasks];

    if (!updatesArray.length) {
      return;
    }

    const updatesMap = new Map(
      updatesArray.filter((item) => item?.id).map((item) => [item.id, item])
    );

    if (!updatesMap.size) {
      return;
    }

    // Actualizar la tarea en el estado local de las fases
    setPhases((prevPhases) =>
      prevPhases.map((phase) => ({
        ...phase,
        tasks: phase.tasks?.map((task) => {
          const update = updatesMap.get(task.id);
          return update ? { ...task, ...update } : task;
        }),
      }))
    );

    // También actualizar localPhaseTasks si estamos en el kanban de fase
    // para que el cambio se vea inmediatamente en las tarjetas del kanban
    if (showPhaseKanban && selectedPhaseId) {
      setLocalPhaseTasks((prevTasks) =>
        prevTasks.map((task) => {
          const update = updatesMap.get(task.id);
          if (!update) {
            return task;
          }

          return {
            ...task,
            ...update,
            title: update.title || update.name || task.title,
            priority: update.priority || task.priority,
            assignee:
              update.assigned_to_name || update.assignee || task.assignee,
            assigned_to_name: update.assigned_to_name || task.assigned_to_name,
            assigned_to:
              update.assigned_to !== undefined
                ? update.assigned_to
                : task.assigned_to,
            dueDate:
              update.dueDate ||
              update.deadline ||
              task.dueDate ||
              task.deadline,
            deadline:
              update.deadline !== undefined ? update.deadline : task.deadline,
            documentation_urls: update.documentation_urls ?? task.documentation_urls,
          };
        })
      );
    }
  };

  // Obtener ID del cliente activo (para cargar sus datos de del proyecto)
  const activeClientId = React.useMemo(() => {
    if (currentUser?.role === "superadmin") {
      return selectedClient;
    } else if (
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant" ||
      (currentUser?.role === "client" && isMultiClientUser)
    ) {
      return selectedFilter;
    } else if (currentUser?.role === "client") {
      const client = currentUser?.client;
      if (Array.isArray(client)) {
        return client[0]?.id || client[0] || null;
      }
      return client?.id || client || null;
    }
    return null;
  }, [currentUser?.role, selectedClient, selectedFilter, currentUser?.client, isMultiClientUser]);

  // Cargar proyectos (solo para superadmin y admin/consultant sin cliente seleccionado)
  const globalProjectsEndpoint = React.useMemo(() => {
    // Solo para roles que no sean cliente Y cuando NO hay cliente seleccionado
    // Si hay cliente activo o project ID en URL, no necesitamos lista global
    if (activeClientId || urlProjectId) {
      return null;
    }

    if (currentUser?.role === "client" && !isMultiClientUser) {
      return null;
    }

    const params = new URLSearchParams();
    params.set("project_type", "setup");
    params.set("page", globalProjectsPage.toString());
    params.set("page_size", currentSetupPageSize.toString());

    if (currentUser?.role === "superadmin") {
      const agencyParam = advancedAgencyId || selectedFilter;
      if (agencyParam) {
        params.set("agency", agencyParam);
      }
      if (advancedClientId) {
        params.set("client", advancedClientId);
      }
    } else if (
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant" ||
      (currentUser?.role === "client" && isMultiClientUser)
    ) {
      // El backend ya filtra automáticamente por la agencia del usuario
      if (advancedClientId) {
        params.set("client", advancedClientId);
      } else if (selectedFilter) {
        params.set("client", selectedFilter);
      }
    }

    // Agregar filtro de estado si no es "Todos"
    if (selectedStatus && selectedStatus !== "__all__") {
      params.set("state", selectedStatus);
    }

    // Agregar parámetro de búsqueda si existe
    if (searchTerm && searchTerm.trim()) {
      params.set("search", searchTerm.trim());
    }

    return `projects/projects/?${params.toString()}`;
  }, [
    currentUser,
    selectedFilter,
    activeClientId,
    urlProjectId,
    selectedStatus,
    advancedAgencyId,
    advancedClientId,
    globalProjectsPage,
    currentSetupPageSize,
    searchTerm, // Agregar searchTerm como dependencia
  ]);

  // Determinar si debemos hacer fetch del listado global (evita pedir endpoints nulos)
  const shouldFetchGlobalProjects = Boolean(
    interceptorReady && currentUser && !activeClientId && globalProjectsEndpoint
  );

  React.useEffect(() => {
    if (
      !shouldFetchGlobalProjects &&
      !globalProjectsEndpoint &&
      interceptorReady &&
      currentUser &&
      !activeClientId &&
      urlProjectId
    ) {
      console.warn(
        "[TechnicalSetup] Omitiendo fetch de proyectos globales porque el endpoint es null",
        {
          urlProjectId,
          activeClientId,
          selectedFilter,
        }
      );
    }
  }, [
    shouldFetchGlobalProjects,
    globalProjectsEndpoint,
    interceptorReady,
    currentUser,
    activeClientId,
    urlProjectId,
    selectedFilter,
  ]);

  // Cargar proyectos globales cuando NO hay cliente seleccionado
  const {
    data: globalProjectsData,
    isFetching: isFetchingGlobalProjects,
  } = getData(globalProjectsEndpoint, shouldFetchGlobalProjects, [
    globalProjectsEndpoint, // Dependencia del endpoint
    technicalSetupRefreshKey,
    filterRefresh,
    advancedFiltersRefreshKey,
    localSetupRefreshKey,
    searchRefreshKey, // Forzar refetch cuando se borra la búsqueda
  ]);

  React.useEffect(() => {
    if (!globalProjectsData) return;

    if (Array.isArray(globalProjectsData)) {
      setGlobalProjectsList(globalProjectsData);
      setHasMoreGlobalProjects(false);
      setTotalGlobalProjects(globalProjectsData.length);
      return;
    }

    const results = Array.isArray(globalProjectsData?.results)
      ? globalProjectsData.results
      : [];
    const totalCount =
      typeof globalProjectsData?.count === "number"
        ? globalProjectsData.count
        : null;

    setGlobalProjectsList((prev) => {
      if (isTableView || globalProjectsPage === 1) {
        return results;
      }

      const seen = new Set(prev.map((project) => project.id));
      const merged = results.filter((project) => !seen.has(project.id));
      return [...prev, ...merged];
    });
    if (totalCount !== null) {
      const totalPages = Math.ceil(totalCount / currentSetupPageSize);
      setHasMoreGlobalProjects(globalProjectsPage < totalPages);
      setTotalGlobalProjects(totalCount);
    } else {
      setHasMoreGlobalProjects(Boolean(globalProjectsData?.next));
      setTotalGlobalProjects(null);
    }
  }, [globalProjectsData, globalProjectsPage, isTableView, currentSetupPageSize]);

  React.useEffect(() => {
    setGlobalProjectsPage(1);
    setGlobalProjectsList([]);
    setHasMoreGlobalProjects(true);
    setTotalGlobalProjects(null);
  }, [
    selectedStatus,
    selectedFilter,
    advancedAgencyId,
    advancedClientId,
    technicalSetupRefreshKey,
    filterRefresh,
    advancedFiltersRefreshKey,
    localSetupRefreshKey,
    activeClientId,
    urlProjectId,
    currentUser?.role,
    tableRowsPerPage,
    searchTerm, // Agregar searchTerm para resetear la página cuando cambia la búsqueda
  ]);

  React.useEffect(() => {
    setGlobalProjectsPage(1);
  }, [resolvedViewMode]);

  React.useEffect(() => {
    if (isTableView) return;
    if (!shouldFetchGlobalProjects || !loadMoreRef.current) return;
    if (!hasMoreGlobalProjects) return;
    if (globalProjectsList.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingGlobalProjects) {
          setGlobalProjectsPage((prev) => {
            if (!hasMoreGlobalProjects) return prev;
            if (
              typeof totalGlobalProjects === "number" &&
              totalGlobalProjects > 0
            ) {
              const totalPages = Math.ceil(
                totalGlobalProjects / currentSetupPageSize
              );
              if (prev >= totalPages) return prev;
            }
            return prev + 1;
          });
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [
    isTableView,
    shouldFetchGlobalProjects,
    hasMoreGlobalProjects,
    isFetchingGlobalProjects,
    globalProjectsList.length,
    totalGlobalProjects,
    currentSetupPageSize,
  ]);

  // Mapear proyectos globales - el filtrado por estado y búsqueda ya lo hace el backend
  const globalProjects = React.useMemo(() => {
    if (!globalProjectsList.length) return [];

    return globalProjectsList.map((project) => ({
      id: project.id,
      client_name: project.client_name,
      client_logo: project.client_logo,
      setup_variant_display: project.setup_variant_display,
      state: project.state,
      start_date: project.start_date,
      progress_percentage: project.progress_percentage || 0,
      client: project.client,
      name: project.name,
      // Aplicar overrides locales
      ...(localSetupsOverrides[project.id] || {}),
    }));
  }, [globalProjectsList, localSetupsOverrides]);

  // Update status stats from backend response
  useEffect(() => {
    // Use state_stats from backend if available
    if (globalProjectsData?.state_stats) {
      setStatusStats({
        in_progress: globalProjectsData.state_stats.in_progress || 0,
        not_started: globalProjectsData.state_stats.not_started || 0,
        completed: globalProjectsData.state_stats.completed || 0,
        no_necesario: globalProjectsData.state_stats.no_necesario || 0,
        all: globalProjectsData.state_stats.all || 0,
      });
    }
  }, [globalProjectsData]);

  // Badge count based on active status filter
  const badgeCount = React.useMemo(() => {
    if (selectedStatus === "__all__") {
      return statusStats.all;
    }
    return statusStats[selectedStatus] || 0;
  }, [selectedStatus, statusStats]);

  const isLoadingGlobalProjectsInitial =
    isFetchingGlobalProjects && globalProjectsList.length === 0;
  const isLoadingMoreGlobalProjects =
    isFetchingGlobalProjects && globalProjectsPage > 1;

  const isSuperAdmin = currentUser?.role === "superadmin";

  // Función para obtener el ID del proyecto
  const obtainProjectID = () => {
    // Para clientes y otros roles, usar el projectId de la URL
    if (urlProjectId) {
      return urlProjectId;
    }
    return null;
  };

  // Obtener el ID del proyecto
  const projectId = obtainProjectID();

  // Cargar datos del proyecto
  // Solo usa projectId y manualProjectRefresh como dependencias

  const { data: projectData, error: projectError } = getData(
    projectId ? `projects/projects/${projectId}` : null,
    !!projectId && interceptorReady,
    [manualProjectRefresh, technicalSetupRefreshKey]
  );

  React.useEffect(() => {
    if (!projectData?.state) {
      setDetailSetupState(null);
      return;
    }

    setDetailSetupState(projectData.state);
  }, [projectData?.state]);

  const currentDetailSetupState = detailSetupState || projectData?.state || "";

  const runSetupStateChange = React.useCallback(
    async (targetProjectId, newState, previousState) => {
      const updated = await handleSetupStateChange(
        targetProjectId,
        newState,
        previousState
      );

      if (
        updated &&
        projectId &&
        String(targetProjectId) === String(projectId)
      ) {
        setDetailSetupState(newState);
      }
    },
    [handleSetupStateChange, projectId]
  );

  const requestSetupStateChange = React.useCallback(
    (targetProjectId, newState, previousState) => {
      const isActivatingNotNeeded =
        previousState === "no_necesario" && newState !== "no_necesario";
      if (newState === "completed" || isActivatingNotNeeded) {
        setPendingStateChange({
          projectId: targetProjectId,
          newState,
          previousState,
        });
        setStateConfirmOpen(true);
        return;
      }

      runSetupStateChange(targetProjectId, newState, previousState);
    },
    [runSetupStateChange]
  );

  const isPendingActivation =
    pendingStateChange?.previousState === "no_necesario" &&
    pendingStateChange?.newState !== "no_necesario";
  const stateConfirmTitle = isPendingActivation
    ? "Activar setup"
    : "Marcar setup como completado";
  const stateConfirmText = isPendingActivation
    ? `¿Seguro que quieres cambiar el setup a "${setupStateLabels[pendingStateChange.newState]}"? A partir de ahora el setup se empezará a mostrar al cliente.${
        pendingStateChange.newState === "completed" &&
        hasAgencyFeature(currentUser, FEATURE_RECURRING_PROJECTS)
          ? " Además, se activará el plan de recurrencia, que consiste en crear proyectos mensuales para el cliente."
          : ""
      }`
    : completeSetupConfirmText;

  const handleConfirmSetupStateChange = React.useCallback(async () => {
    if (!pendingStateChange) return;

    const { projectId: targetProjectId, newState, previousState } =
      pendingStateChange;
    setStateConfirmOpen(false);
    setPendingStateChange(null);
    await runSetupStateChange(targetProjectId, newState, previousState);
  }, [pendingStateChange, runSetupStateChange]);

  const handleDetailSetupStateAction = React.useCallback(() => {
    if (!projectId) return;

    if (currentDetailSetupState === "not_started") {
      requestSetupStateChange(projectId, "in_progress", currentDetailSetupState);
      return;
    }

    if (currentDetailSetupState === "in_progress") {
      requestSetupStateChange(projectId, "completed", currentDetailSetupState);
    }
  }, [projectId, currentDetailSetupState, requestSetupStateChange]);

  // Extraer client_id del proyecto cuando navegamos directamente a /setup/project_id
  const clientIdFromProject = React.useMemo(() => {
    // Si ya tenemos activeClientId (filtro preseleccionado), no extraer del proyecto
    if (activeClientId) {
      return null;
    }
    // Si tenemos projectData y urlProjectId (navegación directa), extraer client_id
    if (urlProjectId && projectData?.client) {
      return projectData.client;
    }
    return null;
  }, [activeClientId, urlProjectId, projectData?.client]);

  // ID final del cliente para cargar sus datos (prioriza el cliente del proyecto si hay urlProjectId)
  const finalClientId = projectData?.client || activeClientId;

  // Cargar datos del cliente específico del cliente seleccionado para el setup técnico
  const {
    data: clientSetupData,
    error: clientSetupError,
    isLoading: clientSetupIsLoading,
    isInitialLoading: clientSetupIsInitialLoading,
  } = getData(
    finalClientId ? `clients/${finalClientId}` : null,
    !!finalClientId && interceptorReady,
    [finalClientId, clientRefresh, technicalSetupRefreshKey]
  );

  const clientSetupErrorMessage = getApiErrorMessage(clientSetupError);
  const normalizedClientSetupErrorMessage = clientSetupErrorMessage
    .toLowerCase()
    .trim();
  const isClientSetupLoading =
    !clientSetupError &&
    clientSetupData === undefined &&
    (clientSetupIsLoading || clientSetupIsInitialLoading);
  const hasClientSetupData = clientSetupData !== undefined;
  const clientSetupDataMissingProject =
    hasClientSetupData &&
    (!clientSetupData || !clientSetupData?.setup_project_info?.id);
  const clientSetupErrorMissingProject =
    normalizedClientSetupErrorMessage.includes(CLIENT_NO_SETUP_PROJECT_MESSAGE);
  const shouldShowClientItineraryEmptyState =
    isClientUser &&
    !urlProjectId &&
    !projectId &&
    !isClientSetupLoading &&
    (clientSetupErrorMissingProject || clientSetupDataMissingProject);
  const shouldShowClientSetupSkeleton =
    isClientUser && !urlProjectId && !projectId && isClientSetupLoading;

  // Preparar consultores y admins disponibles (usando los datos que ya vienen en clientSetupData)
  const availableConsultants = React.useMemo(() => {
    const consultants = clientSetupData?.consultant_details || [];
    const adminDetailsRaw = clientSetupData?.admin_details;

    // Normalizar admin_details a array (puede venir como objeto o array)
    let admins = [];
    if (adminDetailsRaw) {
      if (Array.isArray(adminDetailsRaw)) {
        admins = adminDetailsRaw;
      } else {
        admins = [adminDetailsRaw];
      }
    }

    // Combinar admins primero, luego consultores
    const combined = [
      ...admins.map((admin) => ({
        id: admin.id,
        full_name: admin.full_name,
        email: admin.email,
        role: admin.role,
        profile_image: admin.profile_image,
      })),
      ...consultants.map((consultant) => ({
        id: consultant.id,
        full_name: consultant.full_name,
        email: consultant.email,
        role: consultant.role,
        profile_image: consultant.profile_image,
      })),
    ];

    return combined;
  }, [clientSetupData?.consultant_details, clientSetupData?.admin_details]);

  // Cuando se carga clientSetupData y obtenemos el project_id, navegar a /setup/projectId
  React.useEffect(() => {
    if (!urlProjectId && clientSetupData?.setup_project_info?.id) {
      const projectIdToNavigate = clientSetupData.setup_project_info.id;

      // Para superadmin: cuando selecciona un cliente
      if (currentUser?.role === "superadmin" && selectedClient) {
        navigate(`/setup/${projectIdToNavigate}`, { replace: true });
      }

      // Para agency_admin y consultant: cuando seleccionan un filtro (cliente) que no sea "global"
      else if (
        (currentUser?.role === "agency_admin" ||
          currentUser?.role === "consultant" ||
          (currentUser?.role === "client" && isMultiClientUser)) &&
        selectedFilter &&
        selectedFilter !== "global"
      ) {
        navigate(`/setup/${projectIdToNavigate}`, { replace: true });
      }

      // Para clientes: navegar automáticamente cuando carga su proyecto (solo si NO es multicliente o si el filtro lo fuerza)
      else if (currentUser?.role === "client" && !isMultiClientUser) {
        navigate(`/setup/${projectIdToNavigate}`, { replace: true });
      }
    }
  }, [
    clientSetupData,
    selectedClient,
    selectedFilter,
    urlProjectId,
    currentUser?.role,
    navigate,
  ]);

  // Función para mapear iconos basados en el orden/tipo de fase
  const getPhaseIcon = (phaseOrder, phaseName) => {
    const icons = [Target, Megaphone, Globe, Share2, PenTool, Zap, Rocket];
    return icons[phaseOrder - 1] || Settings;
  };

  // Mapear datos del proyecto al formato esperado por los componentes
  const mapProjectDataToPhases = React.useMemo(() => {
    if (!projectData || !projectData.phases) {
      return []; // Fallback a datos mock si no hay datos del proyecto
    }

    return projectData.phases.map((phase) => ({
      id: phase.id,
      title: phase.name,
      description:
        phase.description ||
        `Fase ${phase.order} del proyecto de setup técnico`,
      status: phase.state || "not_started",
      icon: getPhaseIcon(phase.order, phase.name),
      progress: Math.round(phase.progress_percentage || 0),
      order: phase.order, // Agregar el order de la fase
      tasks:
        phase.tasks?.map((task) => ({
          id: task.id,
          title: task.name,
          description: task.description,
          status: task.state || "Pendiente",
          icon:
            task.state === "no_necesario"
              ? Settings
              : task.priority === "high"
                ? AlertCircle
                : Settings,
          deadline: task.deadline,
          order: task.order,
          assigned_to: task.assigned_to,
          assigned_to_name: task.assigned_to_name,
          priority: task.priority || "medium",
          documentation_urls: task.documentation_urls || [],
          google_calendar_link: task.google_calendar_link || "",
        })) || [],
    }));
  }, [projectData]);

  // Actualizar las fases cuando cambien los datos del proyecto
  React.useEffect(() => {
    setPhases(mapProjectDataToPhases);
  }, [mapProjectDataToPhases]);

  React.useEffect(() => {
    if (!projectData) {
      setProjectProgress(0);
      return;
    }

    setProjectProgress(Math.round(projectData.progress_percentage || 0));
  }, [projectData]);

  React.useEffect(() => {
    const localProgress = calculateProjectProgress(phases);

    if (localProgress !== null) {
      setProjectProgress(localProgress);
    }
  }, [phases, calculateProjectProgress]);

  const isReadOnlyClient = isClientReadOnly(currentUser, clientSetupData || finalClientId);

  // Volver al listado de setups desde el detalle de un proyecto/cliente
  const handleBackToSetupList = React.useCallback(() => {
    // Si hay un filtro de cliente activo (agency_admin/consultant/cliente multicliente),
    // resetear el filtro a 'global' para poder "volver atrás".
    if (
      (currentUser?.role === "agency_admin" ||
        currentUser?.role === "consultant" ||
        (currentUser?.role === "client" && isMultiClientUser)) &&
      selectedFilter &&
      selectedFilter !== "global"
    ) {
      try {
        changeSelectedFilter("global");
      } catch (e) {
        console.warn("changeSelectedFilter not available:", e);
      }
      return;
    }

    navigate("/setup");
  }, [currentUser?.role, isMultiClientUser, selectedFilter, changeSelectedFilter, navigate]);

  // El refresh cuando cambia el cliente ya está manejado por technicalSetupRefreshKey
  // No necesitamos un useEffect adicional aquí

  // Set page context
  React.useEffect(() => {
    if (currentUser) {
      // Obtener el nombre del cliente desde clientSetupData o desde projectData
      const clientName =
        clientSetupData?.name || projectData?.client_name || "";
      const fallbackClientName =
        selectedFilterData?.id && selectedFilterData.id !== "global"
          ? selectedFilterData.name
          : "";
      const clientTitleName = clientName || fallbackClientName;
      const shouldShowClientNameTitle =
        currentUser.role === "client" &&
        isMultiClientUser &&
        urlProjectId;
      const shouldShowAdminClientNameTitle =
        currentUser.role !== "client" && (urlProjectId || clientSetupData);

      const pageTitle =
        currentUser.role === "client" ? (
          shouldShowClientNameTitle ? (
            <>
              Itinerario:{" "}
              <span
                style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}
              >
                {clientTitleName || "..."}
              </span>
            </>
          ) : (
            "Itinerario"
          )
        ) : shouldShowAdminClientNameTitle ? (
          <>
            Setup técnico:{"  "}
            <span
              style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}
            >
              {clientTitleName || "..."}
            </span>
          </>
        ) : (
          "Setup Técnico"
        );
      const pageSubtitle =
        currentUser.role === "client"
          ? "Seguimiento de tu proceso de implementación"
          : "Configuración técnica y seguimiento de fases";

      const canShowDetailStateAction =
        Boolean(projectId) &&
        !isClientUser &&
        (currentDetailSetupState === "not_started" ||
          currentDetailSetupState === "in_progress");

      const setupStateContent = isReadOnlyClient ? (
        <ReadOnlyBadge />
      ) : currentDetailSetupState === "completed" ? (
          <Badge color="green" size="lg">
            Completado
          </Badge>
        ) : currentDetailSetupState === "no_necesario" && projectId && !isClientUser ? (
          <Button
            size="sm"
            onClick={() => requestSetupStateChange(projectId, "not_started", currentDetailSetupState)}
            sx={{ whiteSpace: "nowrap" }}
          >
            <PlayCircle size={14} style={{ marginRight: "8px" }} />
            Activar Setup
          </Button>
        ) : canShowDetailStateAction ? (
          <Button
            size="sm"
            onClick={handleDetailSetupStateAction}
            sx={{ whiteSpace: "nowrap" }}
          >
            {currentDetailSetupState === "not_started" ? (
              <>
                <PlayCircle size={14} style={{ marginRight: "8px" }} />
                Marcar en progreso
              </>
            ) : (
              <>
                <CheckCircle2 size={14} style={{ marginRight: "8px" }} />
                Marcar como completado
              </>
            )}
          </Button>
        ) : null;

      // En el detalle del setup, el acceso al kanban va junto a la acción de estado
      const rightContent =
        projectId && projectData ? (
          <>
            <Button variant="dark" size="sm" onClick={() => handleViewKanban()}>
              <Kanban size={14} style={{ marginRight: "8px" }} />
              Ver en kanban
            </Button>
            {setupStateContent}
          </>
        ) : (
          setupStateContent
        );

      const projectSwitchesContent = !urlProjectId && (!isClientUser || isMultiClientUser) ? (
        <Badge
          variant="secondary"
          size="lg"
          sx={{ ml: 0.5, borderRadius: "12px", fontWeight: 500, color: "text.secondary" }}
        >
          {badgeCount}
        </Badge>
      ) : null;

      // Generar breadcrumb según el rol y el cliente seleccionado
      let breadcrumbItems = null;

      // Contenido izquierdo: flecha para volver a /setup cuando se está en la vista de un proyecto/cliente
      let leftContent = null;
      if (!isClientUser || isMultiClientUser) {
        // Mostrar la flecha si estamos viendo un proyecto/cliente concreto (navegación directa o cliente cargado)
        if (urlProjectId || clientSetupData) {
          leftContent = (
            <IconButton
              onClick={handleBackToSetupList}
              size="small"
              sx={{
                color: "primary.main",
                padding: "4px",
                "&:hover": { backgroundColor: "action.hover" },
              }}
              disableRipple={false}
            >
              <ChevronLeft size={20} />
            </IconButton>
          );
        }

        // Si estamos en modo urlProjectId (navegación directa), considerar también el filtro
        if (urlProjectId) {
          if (
            selectedFilter &&
            selectedFilterData &&
            currentUser.role === "superadmin"
          ) {
            // Si hay filtro de agencia: Vista Global > Agencia X > Proyecto
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  changeSelectedFilter("global");
                  navigate("/setup");
                },
              },
              {
                label: `Agencia: ${selectedFilterData.name}`,
                onClick: () => {
                  navigate("/setup");
                  // No cambiar el filtro para mantener la agencia seleccionada
                },
              },
              { label: `Setup técnico: ${clientName}` },
            ];
          } else {
            // Sin filtro de agencia: Vista Global > Proyecto
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  if (
                    currentUser.role === "agency_admin" ||
                    currentUser.role === "consultant"
                  ) {
                    changeSelectedFilter("global");
                  }
                  navigate("/setup");
                },
              },
              { label: `Setup técnico: ${clientName}` },
            ];
          }
        } else if (currentUser.role === "superadmin") {
          if (clientSetupData) {
            // Hay un cliente seleccionado
            if (selectedFilter && selectedFilterData) {
              // Vista Global > Agencia X > Cliente X
              breadcrumbItems = [
                {
                  label: "Vista Global",
                  onClick: () => {
                    changeSelectedFilter("global");
                    setSelectedClient(null);
                    navigate("/setup");
                  },
                },
                {
                  label: `Agencia: ${selectedFilterData.name}`,
                  onClick: () => setSelectedClient(null),
                },
                { label: `Cliente: ${clientSetupData.name}` },
              ];
            } else {
              // Vista Global > Cliente X
              breadcrumbItems = [
                {
                  label: "Vista Global",
                  onClick: () => {
                    changeSelectedFilter("global");
                    setSelectedClient(null);
                    navigate("/setup");
                  },
                },
                { label: `Cliente: ${clientSetupData.name}` },
              ];
            }
          } else if (selectedFilter && selectedFilterData) {
            // Solo filtro de agencia sin cliente específico: Vista Global > Agencia X
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  changeSelectedFilter("global");
                  navigate("/setup");
                },
              },
              { label: `Agencia: ${selectedFilterData.name}` },
            ];
          } else {
            // Sin filtro ni cliente: Vista Global
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        } else if (
          currentUser.role === "agency_admin" ||
          currentUser.role === "consultant"
        ) {
          if (clientSetupData) {
            // Hay un cliente seleccionado: Vista Global > Cliente X
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  changeSelectedFilter("global");
                  navigate("/setup");
                },
              },
              { label: `Cliente: ${clientSetupData.name}` },
            ];
          } else if (selectedFilter && selectedFilterData) {
            // Filtro de cliente sin cliente específico seleccionado: Vista Global > Cliente X
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  changeSelectedFilter("global");
                  navigate("/setup");
                },
              },
              { label: `Cliente: ${selectedFilterData.name}` },
            ];
          } else {
            // Sin filtro: Vista Global
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        }
      }

      setPageContext({
        title: pageTitle,
        subtitle: pageSubtitle,
        showTitle: true,
        leftContent: (!isClientUser || isMultiClientUser) ? leftContent : null,
        rightContent: rightContent,
        titleRightContent: projectSwitchesContent,
        breadcrumbItems: isClientUser ? null : breadcrumbItems,
      });
    }
  }, [
    currentUser,
    setPageContext,
    selectedClient,
    clientSetupData,
    selectedFilter,
    selectedFilterData,
    urlProjectId,
    projectId,
    projectData,
    currentDetailSetupState,
    handleDetailSetupStateAction,
    requestSetupStateChange,
    navigate,
    isClientUser,
    neutrals,
    badgeCount,
    isMultiClientUser,
    handleBackToSetupList,
    isReadOnlyClient,
  ]);

  const isReadOnly = currentUser.role === "client";
  const canEditSetup = !isReadOnly && !isReadOnlyClient;

  // Funciones para el Kanban
  const handleViewKanban = () => {
    setManualProjectRefresh((prev) => prev + 1); // Forzar refetch del proyecto
    setShowKanban(true);
  };

  const handleViewPhaseKanban = (phaseId) => {
    setSelectedPhaseId(phaseId);
    setPhaseTasksRefreshTrigger((prev) => prev + 1); // Usar timestamp para forzar refresh siempre

    setShowPhaseKanban(true);
  };

  const handleCloseKanban = () => {
    setManualProjectRefresh((prev) => prev + 1); // Refresh del proyecto al cerrar kanban
    setShowKanban(false);
  };

  const handleClosePhaseKanban = () => {
    setManualProjectRefresh((prev) => prev + 1); // Refresh del proyecto al cerrar kanban de fase
    setShowPhaseKanban(false);
    setSelectedPhaseId(null);
    setLocalPhaseTasks([]); // Limpiar tareas locales al cerrar
  };

  const handleNewTask = () => {
    // Aquí puedes implementar la lógica para crear una nueva fase
    console.log("Crear nueva fase");
  };

  const handleEditPhase = (phase) => {
    setTaskToEdit(phase); // Usar taskToEdit para phase también
    setIsModalFromKanban(false);
    setEditTaskModalOpen(true);
  };

  const handleDeleteTask = (phaseId) => {
    // Aquí puedes implementar la lógica para eliminar una fase
    console.log("Eliminar fase:", phaseId);
  };

  const handleDragStart = (e, phase) => {
    setDraggedTask(phase); // Mantener el nombre de la variable para compatibilidad
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, newKanbanStatus) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== newKanbanStatus) {
      // Mapear el status del Kanban de vuelta al formato de setup técnico
      let newTechnicalStatus = "not_started";
      if (newKanbanStatus === "in_progress") {
        newTechnicalStatus = "in_progress";
      } else if (newKanbanStatus === "completed") {
        newTechnicalStatus = "completed";
      } else if (newKanbanStatus === "not_started") {
        newTechnicalStatus = "not_started";
      } else if (newKanbanStatus === "no_necesario") {
        newTechnicalStatus = "no_necesario";
      }

      // Actualizar el estado de la fase
      updatePhaseStatus(draggedTask.id, newTechnicalStatus);
    }
    setDraggedTask(null);
  };

  // Funciones de drag and drop para tareas de fase
  const handlePhaseTaskDragStart = (e, task) => {
    setDraggedPhaseTask(task);
    e.dataTransfer.effectAllowed = "move";
  };

  const handlePhaseTaskDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handlePhaseTaskDrop = async (e, newKanbanStatus) => {
    e.preventDefault();
    if (draggedPhaseTask && draggedPhaseTask.status !== newKanbanStatus) {
      // Actualizar inmediatamente en el kanban para UX optimizada
      if (kanbanTaskUpdateRef.current) {
        kanbanTaskUpdateRef.current(draggedPhaseTask.id, newKanbanStatus);
      }

      // Mapear el status del Kanban de vuelta al formato del backend
      let newBackendStatus = "planeado";
      if (newKanbanStatus === "in_progress") {
        newBackendStatus = "en_desarrollo";
      } else if (newKanbanStatus === "completed") {
        newBackendStatus = "finalizado";
      } else if (newKanbanStatus === "not_started") {
        newBackendStatus = "planeado";
      } else if (newKanbanStatus === "no_necesario") {
        newBackendStatus = "no_necesario";
      }

      // Actualizar el estado de la tarea usando la función existente
      // showPopup=true para mostrar feedback al usuario en el Kanban
      const success = await updateTaskStatus(
        selectedPhaseId,
        draggedPhaseTask.id,
        newBackendStatus,
        true
      );

      // En caso de error, revertir el cambio local
      if (!success && kanbanTaskUpdateRef.current) {
        kanbanTaskUpdateRef.current(
          draggedPhaseTask.id,
          draggedPhaseTask.status
        );
      }
    }
    setDraggedPhaseTask(null);
  };

  // Cargar tareas de la fase seleccionada para el kanban
  const { data: selectedPhaseTasksData, error: selectedPhaseTasksError } =
    getData(
      selectedPhaseId ? `projects/tasks/?phase=${selectedPhaseId}` : null,
      !!selectedPhaseId && interceptorReady,
      [selectedPhaseId, phaseTasksRefreshTrigger, technicalSetupRefreshKey]
    );

  // Obtener todas las fases para el Kanban
  const getAllPhases = () => {
    return phases.map((phase, index) => {
      // Mapear el status de las fases técnicas al formato del Kanban
      let kanbanStatus = "not_started";
      if (phase.status === "in_progress") {
        kanbanStatus = "in_progress";
      } else if (phase.status === "completed") {
        kanbanStatus = "completed";
      } else if (phase.status === "not_started") {
        kanbanStatus = "not_started";
      } else if (phase.status === "no_necesario") {
        kanbanStatus = "no_necesario";
      }

      // Filtrar tareas que no tienen estado "no_necesario" para contar tareas válidas
      const validTasks = phase.tasks
        ? phase.tasks.filter((task) => task.status !== "no_necesario")
        : [];
      const totalTasks = validTasks.length;
      const completedTasks = validTasks.filter(
        (task) => task.status === "finalizado"
      ).length;

      return {
        id: phase.id,
        title: `${index + 1}. ${phase.title}`,
        status: kanbanStatus,
        tasksCount: totalTasks,
        completedTasksCount: completedTasks,
        progress: phase.progress,
        icon: phase.icon,
        phaseNumber: index + 1,
      };
    });
  };

  // Actualizar tareas locales cuando se cargan datos de la API
  React.useEffect(() => {
    if (selectedPhaseTasksData) {
      const mappedTasks = selectedPhaseTasksData.map((task) => ({
        id: task.id,
        title: task.name,
        status: (() => {
          switch (task.state) {
            case "pendiente":
              return "not_started";
            case "en_desarrollo":
              return "in_progress";
            case "finalizado":
              return "completed";
            case "no_necesario":
              return "no_necesario";
            default:
              return "not_started";
          }
        })(),
        priority: task.priority || "medium",
        assignee: task.assigned_to_name || "Sin asignar",
        assigned_to: task.assigned_to, // Añadir para consistencia con gestión
        assigned_to_name: task.assigned_to_name,
        dueDate: task.deadline,
        description: task.description,
        documentation_urls: task.documentation_urls || [],
        google_calendar_link: task.google_calendar_link || "",
      }));
      setLocalPhaseTasks(mappedTasks);
    } else {
      // Limpiar tareas locales si no hay datos de la API
      setLocalPhaseTasks([]);
    }
  }, [selectedPhaseTasksData]);

  // Función para actualizar el estado de una tarea localmente
  const updatePhaseTaskStatusLocally = (taskId, newStatus) => {
    setLocalPhaseTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    );
  };

  // Obtener tareas de fase para el kanban (usando estado local)
  const getPhaseTasks = () => {
    return localPhaseTasks;
  };

  // Función para manejar el clic en una tarjeta de proyecto
  const handleProjectClick = React.useCallback((project) => {
    // Para superadmin: actualizar selectedClient para que se carguen los datos del cliente
    if (currentUser?.role === "superadmin" && project.client) {
      setSelectedClient(project.client);
    }
    // Para agency_admin y consultant: NO cambiar el filtro, solo navegar al proyecto
    // Navegar directamente a la ruta con el ID del proyecto
    navigate(`/setup/${project.id}`);
  }, [currentUser?.role, navigate, setSelectedClient]);

  const handleSetupViewModeChange = React.useCallback((nextViewMode) => {
    setViewMode(nextViewMode);
    setGlobalProjectsPage(1);
  }, []);

  const handleSetupTablePageChange = React.useCallback((newPage) => {
    setGlobalProjectsPage(newPage + 1);
  }, []);

  const handleSetupTableRowsPerPageChange = React.useCallback((nextRowsPerPage) => {
    setTableRowsPerPage(nextRowsPerPage);
    setGlobalProjectsPage(1);
  }, []);

  // Mostrar vista global de proyectos cuando no hay cliente seleccionado Y no hay projectId en URL (para admin/consultant roles)
  if (!activeClientId && !urlProjectId && (currentUser?.role !== "client" || isMultiClientUser)) {
    return (
      <>
        <GlobalProjectsView
          projects={globalProjects}
          onProjectClick={handleProjectClick}
          theme={theme}
          isLoading={isLoadingGlobalProjectsInitial}
          isLoadingMore={isLoadingMoreGlobalProjects}
          isFetching={isFetchingGlobalProjects}
          hasMore={hasMoreGlobalProjects}
          loadMoreRef={loadMoreRef}
          selectedStatus={selectedStatus}
          onStatusChange={(value) => {
            setSelectedStatus(value);
            // Forzar refresco y limpiar overrides al cambiar de tab
            setLocalSetupRefreshKey((prev) => prev + 1);
            setLocalSetupsOverrides({});
          }}
          searchInput={searchInput}
          onSearchInputChange={(value) => setSearchInput(value)}
          filterChips={displayedFilterChips}
          onOpenFilterModal={handleOpenFilterModal}
          onStateChange={requestSetupStateChange}
          viewMode={resolvedViewMode}
          onViewModeChange={handleSetupViewModeChange}
          showViewModeSelector={!isClientUser}
          totalProjects={totalGlobalProjects}
          currentPage={globalProjectsPage}
          rowsPerPage={currentSetupPageSize}
          onTablePageChange={handleSetupTablePageChange}
          onTableRowsPerPageChange={handleSetupTableRowsPerPageChange}
          statusStats={statusStats}
          filterModalProps={{
            open: isFilterModalOpen,
            onClose: handleCloseFilterModal,
            title: "Filtros avanzados",
            filterConfigs,
            draftFilters: draftAdvancedFilters,
            onChange: handleDraftFilterChange,
            onApply: handleApplyAdvancedFilters,
            onClearAll: handleClearDraftFilters,
          }}
        />
        <ConfirmModal
          open={stateConfirmOpen}
          onClose={() => {
            setStateConfirmOpen(false);
            setPendingStateChange(null);
          }}
          onConfirm={handleConfirmSetupStateChange}
          title={stateConfirmTitle}
          description={stateConfirmText}
          confirmLabel="Confirmar"
        />
        <SuccessPopup
          message={popupMessage}
          isOpen={popupOpen}
          onClose={() => setPopupOpen(false)}
          error={isError}
        />
      </>
    );
  }

  if (shouldShowClientSetupSkeleton) {
    return clientSetupLoadingSkeleton;
  }

  if (shouldShowClientItineraryEmptyState) {
    return (
      <EmptyStateContainer
        icon={Settings}
        title="Itinerario no disponible"
        message={getSetupUnavailableMessage(currentUser)}
        showIconBackground={false}
        iconColor={theme.palette.text.secondary}
        showBadge={false}
      />
    );
  }

  // Mostrar mensaje cuando no hay cliente seleccionado Y no hay projectId en URL (solo para clientes)
  if (!activeClientId && !urlProjectId) {
    return (
      <Card>
        <CardContent sx={{ p: 6 }}>
          <EmptyStateMessage
            icon={Users}
            message="Cargando información del cliente..."
            iconSize={48}
          />
        </CardContent>
      </Card>
    );
  }

  const projectLoadingSkeleton = (
    <Stack spacing={3}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1 }} />
          <Skeleton variant="rounded" height={160} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="rounded" height={12} sx={{ mt: 2 }} />
          <Stack spacing={2} sx={{ mt: 3 }}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton
                key={`phase-skeleton-${idx}`}
                variant="rounded"
                height={72}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  const clientSetupLoadingSkeleton = (
    <Stack spacing={3}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Skeleton variant="text" width="40%" height={28} />
            <Skeleton variant="rounded" width={120} height={36} />
          </Box>
          <Skeleton variant="text" width="25%" height={20} sx={{ mt: 3 }} />
          <Skeleton variant="rounded" height={12} sx={{ mt: 1.5 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 3,
            }}
          >
            {Array.from({ length: 2 }).map((_, idx) => (
              <Box key={`client-skeleton-metric-${idx}`}>
                <Skeleton variant="text" width="35%" height={28} />
                <Skeleton
                  variant="text"
                  width="60%"
                  height={20}
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
              mt: 3,
              p: 2,
            }}
          >
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton variant="text" width="50%" height={18} sx={{ mt: 1 }} />
          </Box>
        </CardContent>
      </Card>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 3,
        }}
      >
        {Array.from({ length: 2 }).map((_, columnIdx) => (
          <Card key={`client-skeleton-panel-${columnIdx}`}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Skeleton variant="text" width="45%" height={22} />
                <Skeleton variant="rounded" width={80} height={28} />
              </Box>
              <Stack spacing={2}>
                {Array.from({ length: 3 }).map((_, itemIdx) => (
                  <Card
                    key={`client-skeleton-item-${columnIdx}-${itemIdx}`}
                    sx={{ border: `1px solid ${neutrals.gray[200]}` }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Skeleton variant="text" width="70%" height={18} />
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={16}
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Stack>
  );

  // Mostrar mensaje de carga cuando se están cargando los datos del proyecto
  if (
    (activeClientId || urlProjectId) &&
    !projectData &&
    !projectError &&
    projectId
  ) {
    return projectLoadingSkeleton;
  }

  // Mostrar mensaje de error si hay un error al cargar el proyecto
  if (projectError) {
    return (
      <EmptyStateContainer
        title="Error al cargar el proyecto"
        message="No se pudo obtener la información del setup técnico. Intenta de nuevo."
        badgeText="Error"
      />
    );
  }

  // Mostrar mensaje cuando el cliente no tiene un proyecto de setup (solo si no hay urlProjectId)
  if (!isClientUser && activeClientId && !projectId && !urlProjectId) {
    return (
      <EmptyStateContainer
        icon={Settings}
        title="Sin proyecto de setup"
        message="Este cliente no tiene un proyecto de setup técnico asignado."
        showIconBackground={false}
        iconColor={theme.palette.text.secondary}
        showBadge={false}
      />
    );
  }

  // Verificar que el cliente del proyecto coincida con el filtro seleccionado (para agency_admin/consultant)
  if (
    urlProjectId &&
    projectData &&
    (currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant") &&
    selectedFilter &&
    selectedFilter !== "global" &&
    projectData.client !== selectedFilter
  ) {
    return (
      <EmptyStateContainer
        title="Proyecto no accesible"
        message="Este proyecto pertenece a un cliente diferente al seleccionado en el filtro general."
        badgeText="No accesible"
      />
    );
  }

  // Verificar que el cliente del proyecto pertenece a la agencia seleccionada (para superadmin)
  if (
    urlProjectId &&
    projectData &&
    currentUser?.role === "superadmin" &&
    selectedFilter &&
    selectedFilter !== "global" &&
    clientSetupData &&
    clientSetupData.agency !== selectedFilter
  ) {
    return (
      <EmptyStateContainer
        title="Proyecto no accesible"
        message="Este proyecto pertenece a una agencia diferente a la seleccionada en el filtro general."
        badgeText="No accesible"
      />
    );
  }

  return (
    <Box sx={{ pb: { xs: "80px", md: 0 } }}>
      {showKanban ? (
        <KanbanView
          project={{
            id: "technical-setup-project",
            name: "Setup Técnico",
            clientName:
              clientSetupData?.name || projectData?.client_name || "Cliente",
            status: "in-progress",
          }}
          tasks={getAllPhases()} // Cambiado de getAllTasks() a getAllPhases()
          currentUser={currentUser}
          onClose={handleCloseKanban}
          isPhaseView={true} // Añadido para indicar que son fases
          readOnly={isReadOnlyClient}
          onNewTask={handleNewTask}
          onEditTask={handleEditPhase}
          onDeleteTask={handleDeleteTask}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
      ) : showPhaseKanban ? (
        <KanbanView
          project={{
            id: "technical-setup-project",
            name: "Setup Técnico",
            clientName:
              clientSetupData?.name || projectData?.client_name || "Cliente",
            status: "in-progress",
          }}
          tasks={getPhaseTasks()} // Usar las tareas mapeadas desde TechnicalSetup
          currentUser={currentUser}
          onClose={handleClosePhaseKanban}
          isPhaseTaskView={true} // Nuevo: indica que son tareas de una fase específica
          readOnly={isReadOnlyClient}
          onTaskStatusUpdate={updatePhaseTaskStatusLocally} // Función para actualizar localmente
          phaseNumber={
            phases.find((phase) => phase.id === selectedPhaseId)?.order
          }
          phaseName={
            phases.find((phase) => phase.id === selectedPhaseId)?.title
          }
          onNewTask={handleNewTask}
          onEditTask={(task) => handleEditTask(task, true)}
          onDeleteTask={handleDeleteTask}
          onDragStart={handlePhaseTaskDragStart}
          onDragOver={handlePhaseTaskDragOver}
          onDrop={handlePhaseTaskDrop}
        />
      ) : (
        <>
          {/* Contenido principal */}
          {currentDetailSetupState === "no_necesario" && (
            <Card sx={{ mb: 3, borderColor: alpha(theme.palette.text.secondary, 0.3) }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                      flexShrink: 0,
                    }}
                  >
                    <AlertCircle size={20} color={theme.palette.text.secondary} />
                  </Box>
                  <Box>
                    <Typography
                      component="h3"
                      sx={{ fontSize: "0.95rem", fontWeight: 600, color: theme.palette.text.primary }}
                    >
                      Setup marcado como &ldquo;No Necesario&rdquo;
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.85rem", color: theme.palette.text.secondary, mt: 0.5 }}
                    >
                      El cliente no ve este setup y sus fases y tareas no pueden editarse mientras
                      esté en este estado. Pulsa &ldquo;Activar Setup&rdquo; para empezar a trabajarlo.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
          <SetupDetailView
            phases={phases}
            projectProgress={projectProgress}
            setupState={currentDetailSetupState}
            startDate={projectData?.start_date}
            isClient={isReadOnly}
            locked={currentDetailSetupState === "no_necesario"}
            onViewPhaseKanban={handleViewPhaseKanban}
            onUpdatePhaseStatus={canEditSetup ? updatePhaseStatus : undefined}
            onUpdateTaskStatus={
              canEditSetup
                ? (phaseId, taskId, newStatus) => updateTaskStatus(phaseId, taskId, newStatus, true)
                : undefined
            }
            onEditTask={canEditSetup ? (task) => handleEditTask(task, false) : undefined}
            onEditPhase={canEditSetup ? (phase) => handleEditPhase(phase) : undefined}
          />
        </>
      )}

      {/* Modal de edición de tareas - disponible en todas las vistas */}
      <EditTaskModal
        open={editTaskModalOpen}
        onClose={handleCloseEditModal}
        task={taskToEdit}
        consultants={availableConsultants}
        onTaskUpdated={handleTaskUpdated}
        onSuccessPopup={(message) => {
          setPopupMessage(message);
          setIsError(false);
          setPopupOpen(true);
        }}
        onErrorPopup={(message) => {
          setPopupMessage(message);
          setIsError(true);
          setPopupOpen(true);
        }}
        isFromKanban={isModalFromKanban}
        isPhaseEdit={!!taskToEdit?.tasks} // Si taskToEdit tiene tasks, es una fase
      />

      <ConfirmModal
        open={stateConfirmOpen}
        onClose={() => {
          setStateConfirmOpen(false);
          setPendingStateChange(null);
        }}
        onConfirm={handleConfirmSetupStateChange}
        title={stateConfirmTitle}
        description={stateConfirmText}
        confirmLabel="Confirmar"
      />

      {/* Popup de notificación */}
      <SuccessPopup
        message={popupMessage}
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        error={isError}
        highPriority={showKanban || showPhaseKanban}
      />
    </Box>
  );
}
